import React, { useState } from 'react';
import { getTrndsClientSongTiktokStatsById, TrndsClientSongTiktokStats } from '@round/api';
import styles from './SongDetails.module.css';
import { Button, Image, Skeleton } from '@round/ui-kit';
import { ReactComponent as MusicIcon } from '../../../../../assets/MusicIcon.svg';
import cn from 'classnames';
import MetricCard from '../../../../../ui/DataDisplay/Statistics/MetricCard/MetricCard';
import MusicNoteIcon from '../../../../../SharedComponents/svg/Icons/MusicNoteIcon';
import VideoCameraIcon from '../../../../../SharedComponents/svg/Icons/VideoCameraIcon';
import CalendarMonthIcon from '../../../../../SharedComponents/svg/Icons/CalendarMonthIcon';
import SongCreationsGraph from '../../SongDetailsModal/SongCreationsGraph/SongCreationsGraph';
import TopTiktokAudios from './TopTiktokAudios/TopTiktokAudios';
import useAbortableEffect from '../../../../../Hooks/useAbortableEffect';
import { showNotification } from '../../../../../helpers';
import AddClientSongToCollection from '../../AddClientSongToCollection/AddClientSongToCollection';
import TopPosts from './TopPosts/TopPosts';

type SongDetailsProps = {
    songId: number;
    clientId: number;
    songStats?: TrndsClientSongTiktokStats | null;
};

const SongDetails = ({ songStats, songId, clientId }: SongDetailsProps) => {
    const [stats, setStats] = useState<TrndsClientSongTiktokStats | null>(songStats ?? null);
    const [isStatsLoading, setIsStatsLoading] = useState(false);

    useAbortableEffect(
        (signal) => {
            if (!songStats) {
                setIsStatsLoading(true);
                getTrndsClientSongTiktokStatsById(clientId, songId, { signal })
                    .then((response) => {
                        if (response.status === 200) {
                            setStats(response.data);
                            return;
                        }
                    })
                    .catch((err: any) => {
                        if (err.name === 'AbortError') {
                            return;
                        }

                        showNotification('Could not fetch data', 'error');
                    })
                    .finally(() => setIsStatsLoading(false));
            }
        },
        [clientId, songId, songStats]
    );

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.song}>
                    <Image
                        className={styles.songImage}
                        loading={isStatsLoading}
                        src={stats?.song.image ?? ''}
                        alt={stats?.song.title}
                    />
                    <div className={styles.songInfo}>
                        <a
                            className={cn(styles.songTitle, {
                                [styles.songActive]: stats?.song.spotify_song_url,
                            })}
                            href={stats?.song.spotify_song_url}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {isStatsLoading ? <Skeleton width="10rem" /> : stats?.song.title}
                        </a>
                        <span className={styles.songAuthors}>
                            {isStatsLoading ? (
                                <Skeleton width="7rem" />
                            ) : (
                                <>
                                    <MusicIcon className={styles.musicIcon} />
                                    {stats?.song.artists.map((a) => a.name).join(', ')}
                                </>
                            )}
                        </span>
                    </div>
                </div>

                {stats && (
                    <AddClientSongToCollection
                        clientId={clientId}
                        songId={stats.song.id}
                        collectionsId={stats.song_collection_ids}
                        renderAnchor={({ setRef }) => (
                            <Button ref={setRef} className={styles.actionButton}>
                                + Add to collection
                            </Button>
                        )}
                    />
                )}
            </div>

            <div className={styles.headlineStats}>
                <MetricCard
                    loading={isStatsLoading}
                    baseColor="#0090FF"
                    value={stats?.audio_count}
                    label="Total audios tracked"
                    Icon={MusicNoteIcon}
                />
                <MetricCard
                    loading={isStatsLoading}
                    baseColor="#F7685B"
                    value={stats?.total_video_count}
                    label="Total video creations"
                    Icon={VideoCameraIcon}
                />
                <MetricCard
                    loading={isStatsLoading}
                    baseColor="#FFA114"
                    value={stats?.total_video_count_weekly_change}
                    label="creations in the last 7 days"
                    Icon={CalendarMonthIcon}
                />
                <MetricCard
                    loading={isStatsLoading}
                    baseColor="#48D98A"
                    value={stats?.total_video_count_daily_change}
                    label="daily creations"
                    Icon={CalendarMonthIcon}
                />
            </div>

            <SongCreationsGraph data={stats?.standardised_data} loading={isStatsLoading} />

            <TopTiktokAudios songId={songId} clientId={clientId} />
            <TopPosts clientId={clientId} songId={songId} />
        </div>
    );
};

export default SongDetails;
